import { Show, createEffect, createMemo, createSignal } from "solid-js";
import { DataStore, DataStoreContext, Flexbox, Icon, Modal, processResponseActions, requireViewContext, showDialog, showProgressDialog } from "st-app";
import { IStudentSelectionInfo, StudentSelectionModal } from "./StudentSelectionModal";
import { IParentSelectionInfo, ParentSelectionModal } from "./ParentSelectionModal";
import { requireRoleDashboardContext } from "../RoleDashboardContext";
import { EntityType, roleDashboard as api } from 'sis-app/api'

import styles from '../sis-role-dashboard.module.css'

type GroupActionProps = {
    id: string;
    size?: 'small' | 'medium' | 'large' | undefined;
    allowDetach?: boolean
}
export function GroupAction(props: GroupActionProps) {
    const ctx = requireRoleDashboardContext();
    const viewCtx = requireViewContext();

    const [showStudentModal, setShowStudentModal] = createSignal(false);
    const [showParentModal, setShowParentModal] = createSignal(false);

    const actionDesc = createMemo(() => {
        const id = props.id;
        return ctx.configuration()?.actions.find(act => act.ID == id);
    });

    const dataStore = actionDesc()?.entityType == EntityType.Parent
        ? new DataStoreContext(undefined, 'K12.ParentGuardian.Parent')
        : new DataStoreContext(undefined, 'K12.Student');

    createEffect(() => {
        if (!showStudentModal()) {
            dataStore.clear();
        }
    });

    createEffect(() => {
        if (!showParentModal()) {
            dataStore.clear();
        }
    });

    function setActiveInactiveFilter() {
        const showAI = actionDesc()?.showActiveInactive;
        if (showAI != null) {
            viewCtx.setServerState('SHOW_ACTIVE_INACTIVE', showAI.toString());
        } else {
            viewCtx.setServerState('SHOW_ACTIVE_INACTIVE', '');
        }
    }

    function tryExecuteAction() {
        const desc = actionDesc();
        if (desc) {
            ctx.setLaunchDetached(false);
            switch (desc.entityType) {
                case EntityType.Student:
                    setActiveInactiveFilter();
                    setShowStudentModal(true);
                    break;
                case EntityType.Parent:
                    setShowParentModal(true);
                    break;
                default:
                    executeAction(undefined);
            }
        }
    }

    function tryExecuteDetach(ev: Event) {
        const desc = actionDesc();
        if (desc) {
            ctx.setLaunchDetached(true);
            switch (desc.entityType) {
                case EntityType.Student:
                    setActiveInactiveFilter();
                    setShowStudentModal(true);
                    break;
                case EntityType.Parent:
                    setShowParentModal(true);
                    break;
                default:
                    executeAction(undefined);
            }
        }
        ev.stopPropagation();
    }

    function handleXmlResponse(xml: HTMLElement): boolean {
        const type = xml.getAttribute('TYPE');

        switch (type) {
            case 'Open_Window':
                if (xml.getAttribute('MODAL') !== 'YES' && !ctx.launchDetached()) {
                    ctx.setViewDrawerTitle(actionDesc()?.description ?? '');
                    ctx.setViewDrawerRequest(xml.innerHTML);
                    return true;
                }
                break;
            case 'Open_Add_Window':
                // Intercept Student Add from Transfer/Add New buttons
                if (!ctx.launchDetached()) {
                    const title = xml.querySelector('REV_DATA_REQUEST REV_VIEW')?.getAttribute('CAPTION')
                        || 'Student Add';
                    ctx.setViewDrawerTitle(title);
                    ctx.setViewDrawerRequest(xml.innerHTML);
                    return true;  // Prevent default overlay handling
                }
                break;
            case 'Redirect':
                const url = xml.getAttribute('URL');
                if (url) {
                    window.open(url, '_blank');
                    return true;
                }
                break;
        }

        return false;
    }

    async function executeAction(data: IStudentSelectionInfo | undefined) {
        if (data) {
            viewCtx.setServerState('StudentGU', data.studentGU ?? '');
            viewCtx.setServerState('StudentSchoolYearGU', data.ssyGU ?? '');
            viewCtx.setServerState('StudentFormattedName', data.stuFormattedName ?? '');
        }

        if (props.id === "K12.LocateStudent") {
            ctx.setShowLocateStudentModal(true);
        }
        else {
            const desc = actionDesc();

            // For student-scoped view tiles with a known ViewGU, open the view directly
            // in the drawer iframe so DataAreaProperties (SSY GU, StudentGU) are established
            // in the iframe's own server context. This prevents FK errors when the view's
            // BO BeforeInsert reads DataAreaProperties that would otherwise be lost crossing
            // the SolidJS → iframe boundary via ExecuteAction → handleXmlResponse.
            showProgressDialog("Loading...",
                api.ExecuteAction(props.id, viewCtx.viewState()).then(res => {
                    processResponseActions(res, { handleXml: handleXmlResponse });
                    setShowStudentModal(false);
                    ctx.triggerRefresh();
                })
            );
        }
    }

    async function executeParentAction(data: IParentSelectionInfo | undefined) {
        if (data) {
            viewCtx.setServerState('ParentGU', data.parentGU ?? '');
        }

        showProgressDialog("Loading...",
            api.ExecuteAction(props.id, viewCtx.viewState()).then(res => {
                processResponseActions(res, { handleXml: handleXmlResponse });
                setShowParentModal(false);
                ctx.triggerRefresh();
            })
        );
    }

    const size = createMemo(() => {
        if (actionDesc()?.icon?.includes('fa-')) {
            return 32;
        }
        else {
            switch (props.size) {
                case 'medium':
                    return 40;
                default:
                    return 48;
            }
        }
    });

    return <Show when={actionDesc()}>
        <Flexbox dir="vertical" middle onClick={tryExecuteAction} class={styles.action} role="button">
            <Show when={props.allowDetach}>
                <div class={styles.detach} role="button" onClick={tryExecuteDetach} >
                    <Icon src="fa fa-external-link" />
                </div>
            </Show>
            <div class={styles.icon}>
                <Icon src={actionDesc()?.icon ?? ''} size={size()} />
            </div>
            <Flexbox.Item as="span" class={styles.label} title={actionDesc()!.description}>{actionDesc()!.description}</Flexbox.Item>
        </Flexbox>
        <Show when={showStudentModal()}>
            <DataStore context={dataStore}>
                <Modal title={actionDesc()?.description} show={[showStudentModal, setShowStudentModal]} >
                    <StudentSelectionModal onClose={executeAction} />
                </Modal>
            </DataStore>
        </Show>
        <Show when={showParentModal()}>
            <DataStore context={dataStore}>
                <Modal title={actionDesc()?.description} show={[showParentModal, setShowParentModal]} >
                    <ParentSelectionModal onClose={executeParentAction} />
                </Modal>
            </DataStore>
        </Show>
    </Show>
}
